import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { ReactElement, useState } from 'react'

import {
   Button,
   Hierarchy,
   SubVariant,
   TextField
} from '@flowwai-frontend/design-system'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import {
   ArrowNarrowIcon,
   MailIcon
} from '@flowwai-frontend/design-system/icons'
import { ClientConfigType } from '@flowwai-frontend/bps-officer-web/iam'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'

import {
   loginMachineEvents,
   loginMachineTags
} from '../../constants/loginConstants'
import {
   LoginMachineSend,
   LoginMachineState
} from '../../machines/LoginMachine'
import { validateEmail } from '../../utils/validationUtils'

import * as Styles from './styles'
import './index.css'

interface LoginWithPhoneNumberProps {
   loginMachineSend: LoginMachineSend
   loginMachineState: LoginMachineState
   clientConfig: ClientConfigType
}

const LoginWithPhoneNumber = (
   props: LoginWithPhoneNumberProps
): ReactElement => {
   const { loginMachineSend, loginMachineState, clientConfig } = props

   const {
      context,
      context: { email: defaultEmail }
   } = loginMachineState

   const [email, setEmail] = useState<string>(defaultEmail)
   const [isValid, setIsValid] = useState(defaultEmail ? true : false)
   const [errorMessage, setErrorMessage] = useState('')

   const customT = useCustomT('auth:login')

   const { theme } = useAppTheme()
   const { loginWithEmail: loginCardTheme } = theme.auth.login

   const errorTransitionClassName = 'login-textfield-error-transition '

   const isDefaultLoginLayout = clientConfig.loginLayout.type === 'DEFAULT'

   const onBlurEmail = (): void => {
      validate(email || '')
   }

   const unsetError = (): void => {
      setIsValid(true)
      setError('')
   }

   const setError = (errorText: string): void => {
      setErrorMessage(errorText)
   }

   const validate = (value: string): void => {
      const validationObject = validateEmail(value)

      if (validationObject.isValid) {
         unsetError()
         setIsValid(true)
      } else {
         setError(validationObject.errorMessage || '')
         setIsValid(false)
      }
   }

   const onChangeEmail = (value: string): void => {
      setEmail(value)

      validate(value)
      loginMachineSend({
         type: loginMachineEvents.onChangeValue
      })
   }

   const onClickSendOTP = (event: React.SyntheticEvent): void => {
      event.preventDefault()
      if (isValid) {
         if (!email) return
         loginMachineSend({
            type: loginMachineEvents.requestEmailOtp,
            email: email
         })
      }
   }

   const renderLeftIcon = (): ReactElement => (
      <div className='mr-8px'>
         <MailIcon />
      </div>
   )

   const renderHeader = (): ReactElement => (
      <>
         <span
            className={cn(
               Styles.loginTextStyles,
               loginCardTheme.loginTextColor
            )}
         >
            {customT('loginText')}
         </span>
         <span
            className={cn(
               Styles.loginDescriptionStyles,
               loginCardTheme.loginDescriptionTextColor
            )}
         >
            {customT('loginDescription')}
         </span>
      </>
   )

   const renderAppLogo = (): ReactElement => (
      <div className={'mb-[32px] mx-auto'}>
         <img
            style={{ width: `${clientConfig.login.logoWidth}px` }}
            alt='App Logo'
            src={clientConfig.login.logoURL}
         />
      </div>
   )

   return (
      <form
         className={cn(Styles.loginContainerStyles)}
         onSubmit={onClickSendOTP}
      >
         {isDefaultLoginLayout ? renderHeader() : renderAppLogo()}

         <TextField
            size='Small'
            onBlur={onBlurEmail}
            errorMessage={errorMessage || context.errorMessage}
            value={email || ''}
            type='email'
            onChange={onChangeEmail}
            label={customT('emailLabel')}
            placeholder={customT('emailPlaceholder')}
            leftElement={renderLeftIcon}
            errorTransitionClassName={errorTransitionClassName}
            autoFocus
         />

         <Button
            type='submit'
            hierarchy={Hierarchy.Primary}
            subVariant={SubVariant.Primary}
            size={'Large'}
            isDisabled={!isValid}
            rightIcon={() => <ArrowNarrowIcon />}
            className='mt-6'
            isLoading={loginMachineState.hasTag(
               loginMachineTags.requestOtpLoading
            )}
         >
            {customT('getOtp')}
         </Button>
      </form>
   )
}

export default observer(LoginWithPhoneNumber)
